(() => {
  const selectors = {
    dialog: '[data-vo-newsletter-popup-dialog]',
    dialogAutoOpen: '[data-auto-open-enabled="true"]',
    form: '.vo-newsletter-popup__form',
    field: '[data-vo-newsletter-popup-field]',
    input: '[data-vo-newsletter-popup-input]',
    error: '[data-vo-newsletter-popup-error]',
    success: '[data-vo-newsletter-success]',
    defaultContent: '[data-vo-newsletter-popup-default]',
    close: '[data-vo-newsletter-popup-close]',
    initialFocus: '[data-vo-newsletter-popup-initial-focus]',
    autoOpen: '[data-vo-newsletter-popup-auto-open]',
    opener: '[data-modal-id="newsletter-popup-dialog"], [data-vo-newsletter-popup-trigger="newsletter-popup-dialog"]',
  };

  const storageKeys = {
    lastSubmit: 'vo-newsletter-last-submit-dialog',
    crmSuppressed: 'vo-newsletter-popup-crm-suppressed',
  };

  const hasCrmCampaignCid = () => {
    const cid = new URLSearchParams(window.location.search).get('cid') || '';
    return cid.toUpperCase().startsWith('CM-');
  };

  const getCrmSuppressionState = (dialog) => {
    let isSuppressed = dialog.dataset.crmSuppressed === 'true' || hasCrmCampaignCid();

    try {
      if (isSuppressed) {
        sessionStorage.setItem(storageKeys.crmSuppressed, 'true');
      } else {
        isSuppressed = sessionStorage.getItem(storageKeys.crmSuppressed) === 'true';
      }
    } catch (error) {
      // no-op: sessionStorage can be unavailable in privacy-restricted contexts
    }

    return isSuppressed;
  };

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

  const focusInitialElement = (dialog) => {
    const initialFocusTarget =
      dialog.querySelector(selectors.success) ||
      dialog.querySelector(selectors.initialFocus);
    initialFocusTarget?.focus({ preventScroll: true });
  };

  const openDialog = (dialog) => {
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    dialog.showModal();
    focusInitialElement(dialog);
  };

  const bindForm = (dialog) => {
    const form = dialog.querySelector(selectors.form);
    const field = dialog.querySelector(selectors.field);
    const input = dialog.querySelector(selectors.input);
    const error = dialog.querySelector(selectors.error);

    if (!form || !field || !input || !error) return;
    if (form.dataset.voNewsletterPopupBound === 'true') return;

    form.dataset.voNewsletterPopupBound = 'true';

    const toggleFilledState = () => {
      field.classList.toggle('vo-newsletter-popup__field--filled', input.value.trim() !== '');
    };

    const clearError = () => {
      field.classList.remove('vo-newsletter-popup__field--error');
      error.textContent = '';
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    };

    const showError = (message) => {
      field.classList.add('vo-newsletter-popup__field--error');
      error.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');

      if (error.id) {
        input.setAttribute('aria-describedby', error.id);
      }
    };

    const validate = () => {
      const value = input.value.trim();

      if (value === '') {
        showError(input.dataset.emptyError || 'This field cannot be empty');
        return false;
      }

      if (!emailPattern.test(value)) {
        showError(input.dataset.invalidError || 'Please enter a valid e-mail address');
        return false;
      }

      input.value = value;
      clearError();
      return true;
    };

    toggleFilledState();

    input.addEventListener('input', () => {
      toggleFilledState();

      if (!error.hidden) {
        clearError();
      }
    });

    form.addEventListener('submit', (event) => {
      toggleFilledState();

      if (!validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        input.focus();
        return;
      }

      try {
        sessionStorage.setItem(storageKeys.lastSubmit, dialog.id);
      } catch (error) {
        // no-op: sessionStorage can be unavailable in privacy-restricted contexts
      }
    });
  };

  const resetServerState = (dialog) => {
    const field = dialog.querySelector(selectors.field);
    const input = dialog.querySelector(selectors.input);
    const error = dialog.querySelector(selectors.error);
    const success = dialog.querySelector(selectors.success);
    const defaultContent = dialog.querySelector(selectors.defaultContent);

    dialog.querySelector(selectors.autoOpen)?.remove();
    success?.remove();
    dialog.classList.remove('vo-newsletter-popup--success');
    defaultContent?.removeAttribute('aria-hidden');

    if (error) {
      error.textContent = '';
      error.hidden = true;
    }

    if (input) {
      input.value = input.dataset.initialValue || '';
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }

    if (field) {
      field.classList.remove('vo-newsletter-popup__field--error');
      field.classList.toggle('vo-newsletter-popup__field--filled', (input?.value || '').trim() !== '');
    }
  };

  const bindDialog = (dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.dataset.voNewsletterPopupDialogBound === 'true') return;

    dialog.dataset.voNewsletterPopupDialogBound = 'true';

    bindForm(dialog);

    dialog.querySelector(selectors.close)?.addEventListener('click', () => {
      dialog.close();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close();
      }
    });

    dialog.addEventListener('close', () => {
      const opener = dialog.__voOpenedBy;

      if (opener instanceof HTMLElement) {
        opener.focus({ preventScroll: true });
      }
    });
  };

  const init = () => {
    const dialog = document.querySelector(selectors.dialog);
    if (!(dialog instanceof HTMLDialogElement)) return;

    bindDialog(dialog);

    const isCrmSuppressed = getCrmSuppressionState(dialog);
    if (isCrmSuppressed) {
      dialog.dataset.crmSuppressed = 'true';
    }

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const opener = event.target.closest(selectors.opener);
        if (!opener) return;

        if (isCrmSuppressed) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        dialog.__voOpenedBy = opener;

        window.setTimeout(() => {
          if (dialog.open) {
            focusInitialElement(dialog);
          }
        }, 0);
      },
      true
    );

    let lastSubmitDialog = '';

    try {
      lastSubmitDialog = sessionStorage.getItem(storageKeys.lastSubmit) || '';
    } catch (error) {
      lastSubmitDialog = '';
    }

    const autoOpenInput = dialog.querySelector(selectors.autoOpen);
    const autoOpenReason = autoOpenInput?.value || '';
    const hasPopupSubmitMarker = new URLSearchParams(window.location.search).get('newsletter_popup_submitted') === 'true';

    if (isCrmSuppressed && !hasPopupSubmitMarker) return;

    if (autoOpenInput) {
      if (lastSubmitDialog === dialog.id || (autoOpenReason === 'success' && hasPopupSubmitMarker)) {
        if (dialog.querySelector(selectors.success)) {
          dialog.classList.add('vo-newsletter-popup--success');
          dialog.querySelector(selectors.defaultContent)?.setAttribute('aria-hidden', 'true');
        }

        openDialog(dialog);

        try {
          sessionStorage.removeItem(storageKeys.lastSubmit);
        } catch (error) {
          // no-op: sessionStorage can be unavailable in privacy-restricted contexts
        }
      } else {
        resetServerState(dialog);
      }

      return;
    }

    const sessionEvaluatedKey = dialog.dataset.sessionEvaluatedKey || 'vo-newsletter-popup-session-evaluated';
    let hasEvaluatedSession = false;

    try {
      hasEvaluatedSession = sessionStorage.getItem(sessionEvaluatedKey) === 'true';
    } catch (error) {
      hasEvaluatedSession = false;
    }

    if (hasEvaluatedSession) return;

    try {
      sessionStorage.setItem(sessionEvaluatedKey, 'true');
    } catch (error) {
      // no-op: sessionStorage can be unavailable in privacy-restricted contexts
    }

    if (!dialog.matches(selectors.dialogAutoOpen)) return;

    // Newsletter popup trigger disabled; keep the dialog available without auto-opening it.
    openDialog(dialog);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
